import { useCallback } from 'react';
import { useModernNotification } from '../utils/useModernNotification';

interface UseOrderActionsProps {
  handleDeleteOrder: (orderId: number) => Promise<void>;
  handleViewOrder: (orderId: number) => Promise<void>;
  handleStatusChange: (orderId: number, newStatus: any) => Promise<void>;
  handleBulkStatusChange: (newStatus: any) => Promise<void>;
  handleBulkDelete: () => Promise<void>;
  selectedOrderIds: number[];
  confirm: (options?: any) => Promise<boolean>;
}

export const useOrderActions = ({
  handleDeleteOrder,
  handleViewOrder,
  handleStatusChange,
  handleBulkStatusChange,
  handleBulkDelete,
  selectedOrderIds,
  confirm,
}: UseOrderActionsProps) => {
  const { showSuccess, showError } = useModernNotification();

  const onDeleteOrder = useCallback(
    async (orderId: number) => {
      try {
        const confirmed = await confirm({
          title: '⚠️ Dikkat!',
          message: `#${orderId} numaralı siparişi kalıcı olarak silmek istediğinizden emin misiniz?\n\nBu işlem geri alınamaz ve sipariş geçmişinden tamamen kaldırılacaktır.`,
          confirmText: '🗑️ Evet, Sil',
          cancelText: '🛡️ İptal Et',
          type: 'danger',
        });

        if (!confirmed) {
          return;
        }

        await handleDeleteOrder(orderId);
        showSuccess(`✅ Sipariş #${orderId} başarıyla silindi!`);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Bilinmeyen bir hata oluştu';
        showError(`❌ Sipariş silinirken hata oluştu`, errorMessage);
      }
    },
    [confirm, handleDeleteOrder, showSuccess, showError]
  );

  const onViewOrder = useCallback(
    async (orderId: number) => {
      try {
        await handleViewOrder(orderId);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Sipariş detayı yüklenirken hata oluştu';
        showError('Hata', errorMessage);
      }
    },
    [handleViewOrder, showError]
  );

  const onStatusChange = useCallback(
    async (orderId: number, newStatus: any) => {
      try {
        await handleStatusChange(orderId, newStatus);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Sipariş durumu güncellenirken hata oluştu';
        showError('Hata', errorMessage);
      }
    },
    [handleStatusChange, showError]
  );

  const onBulkStatusChange = useCallback(
    async (newStatus: any) => {
      try {
        const confirmed = await confirm({
          title: '🔄 Toplu Durum Değişikliği',
          message: `${selectedOrderIds.length} adet siparişin durumunu "${
            newStatus === 'preparing'
              ? 'Hazırlanıyor'
              : newStatus === 'on-the-way'
                ? 'Yolda'
                : newStatus === 'completed'
                  ? 'Tamamlandı'
                  : newStatus === 'cancelled'
                    ? 'İptal Edildi'
                    : newStatus
          }" olarak değiştirmek istediğinizden emin misiniz?`,
          confirmText: '✅ Evet, Değiştir',
          cancelText: '❌ İptal Et',
          type: newStatus === 'cancelled' ? 'warning' : 'info',
        });

        if (!confirmed) {
          return;
        }

        await handleBulkStatusChange(newStatus);
        showSuccess(`✅ ${selectedOrderIds.length} sipariş başarıyla güncellendi!`);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Toplu durum güncellemesi sırasında hata oluştu';
        showError('❌ Toplu İşlem Hatası', errorMessage);
      }
    },
    [confirm, handleBulkStatusChange, selectedOrderIds.length, showSuccess, showError]
  );

  const onBulkDelete = useCallback(async () => {
    try {
      const confirmed = await confirm({
        title: '⚠️ Toplu Silme Uyarısı!',
        message: `${selectedOrderIds.length} adet siparişi kalıcı olarak silmek istediğinizden emin misiniz?\n\nBu işlem geri alınamaz ve tüm seçili siparişler geçmişten tamamen kaldırılacaktır.`,
        confirmText: '🗑️ Evet, Hepsini Sil',
        cancelText: '🛡️ İptal Et',
        type: 'danger',
      });

      if (!confirmed) {
        return;
      }

      await handleBulkDelete();
      showSuccess(`✅ ${selectedOrderIds.length} sipariş başarıyla silindi!`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Toplu silme sırasında hata oluştu';
      showError('❌ Toplu Silme Hatası', errorMessage);
    }
  }, [confirm, handleBulkDelete, selectedOrderIds.length, showSuccess, showError]);

  const getStatusDisplayText = useCallback((status: any) => {
    switch (status) {
      case 'preparing':
        return 'Hazırlanıyor';
      case 'on-the-way':
        return 'Yolda';
      case 'completed':
        return 'Tamamlandı';
      default:
        return status;
    }
  }, []);

  return {
    onDeleteOrder,
    onViewOrder,
    onStatusChange,
    onBulkStatusChange,
    onBulkDelete,
    getStatusDisplayText,
  };
};
